import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import CountUp from 'react-countup';
import {
  Plant,
  Code,
  Fingerprint,
  FirstAidKit,
  Broadcast,
  Cpu,
  ArrowRight,
  Quotes,
  CheckCircle,
} from '@phosphor-icons/react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';

const heroImages = [
  'https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/d1labh8s_Car%20Branding%20.jpg',
  'https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/lc18brll_Digital%20Devices%20.png',
  'https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/pqpl0xt6_Street%20Billboard.png',
  'https://customer-assets.emergentagent.com/job_atlas-enterprise-hub/artifacts/0ss7xcd6_3D%20Full%20Color.png',
];

const services = [
  {
    icon: Plant,
    title: 'Smart Agriculture Systems',
    description: 'Intelligent farming solutions with IoT sensors, drone monitoring, and precision agriculture technology.',
    color: '#00C8EB',
  },
  {
    icon: Code,
    title: 'Software Development',
    description: 'Custom enterprise and consumer software tailored to your business needs.',
    color: '#464B8B',
  },
  {
    icon: Fingerprint,
    title: 'Biometric Registration Systems',
    description: 'Advanced biometric identification and registration systems for secure identity management.',
    color: '#00C8EB',
  },
  {
    icon: FirstAidKit,
    title: 'Health App Solutions',
    description: 'HIPAA-compliant digital health tools for modern healthcare providers.',
    color: '#464B8B',
  },
  {
    icon: Broadcast,
    title: 'Satellite Services',
    description: 'Reliable connectivity and data solutions for remote operations.',
    color: '#00C8EB',
  },
  {
    icon: Cpu,
    title: 'IT Infrastructure Setup',
    description: 'Complete IT infrastructure design, deployment, and management for enterprises and institutions.',
    color: '#464B8B',
  },
];

const stats = [
  { value: 2, suffix: '+', label: 'Projects Completed' },
  { value: 98, suffix: '%', label: 'Client Satisfaction' },
  { value: 2, suffix: '+', label: 'Enterprise Clients' },
  { value: 24, suffix: '/7', label: 'Support Available' },
];

const testimonials = [
  {
    quote: "ATLAS SOFTECH transformed our entire IT infrastructure. Their team delivered beyond expectations with remarkable professionalism.",
    author: "Sarah Chen",
    role: "CTO, MedTech Innovations",
    image: "https://images.pexels.com/photos/4342352/pexels-photo-4342352.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    quote: "The satellite solutions provided by ATLAS enabled our remote operations to achieve 99.9% uptime. Game-changing technology.",
    author: "Michael Torres",
    role: "VP Operations, GlobalLink Corp",
    image: "https://images.pexels.com/photos/2216607/pexels-photo-2216607.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
];

const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { theme } = useTheme();

  return (
    <section 
      ref={ref}
      data-testid="stats-section"
      className={`py-20 ${theme === 'dark' ? 'bg-[#12131F]' : 'bg-gray-50'}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-[#00C8EB] stat-number">
                {isInView && (
                  <CountUp
                    end={stat.value}
                    duration={2.5}
                    separator=","
                  />
                )}
                {stat.suffix}
              </div>
              <p className={`mt-2 font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const { theme } = useTheme();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate images every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* Hero Section */}
      <section 
        data-testid="hero-section"
        className={`relative min-h-screen flex items-center overflow-hidden ${
          theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-[#FAFAFC]'
        }`}
      >
        {/* Background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -right-20 w-96 h-96 bg-[#00C8EB]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 -left-20 w-96 h-96 bg-[#464B8B]/10 rounded-full blur-3xl" />
          {/* Grid pattern */}
          <div 
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(${theme === 'dark' ? '#fff' : '#000'} 1px, transparent 1px), linear-gradient(90deg, ${theme === 'dark' ? '#fff' : '#000'} 1px, transparent 1px)`,
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#464B8B]/10 border border-[#464B8B]/20 mb-6">
                <span className="w-2 h-2 rounded-full bg-[#00C8EB] animate-pulse" />
                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Intelligent Technology Solutions
                </span>
              </div>
              
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6 ${
                theme === 'dark' ? 'text-white' : 'text-gray-900'
              }`}>
                Systems{' '}
                <span className="text-gradient">Automation</span>
                {' '}is the future
              </h1>
              
              <p className={`text-lg md:text-xl mb-8 max-w-xl ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                From Conventional to AI high tech systems, ATLAS SOFTECH delivers 
                enterprise-grade technology that powers society and your business growth.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/solutions">
                  <Button
                    data-testid="hero-cta-secondary"
                    size="lg"
                    className="bg-[#00C8EB] hover:bg-[#00C8EB]/90 text-[#0A0A10] font-semibold px-8"
                  >
                    Explore Solutions
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-contact"
                    size="lg"
                    variant="outline"
                    className={`font-semibold px-8 ${
                      theme === 'dark'
                        ? 'border-[#00C8EB] text-[#00C8EB] hover:bg-[#00C8EB]/10'
                        : 'border-[#464B8B] text-[#464B8B] hover:bg-[#464B8B]/10'
                    }`}
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>

              {/* Trust badges */}
              <div className="mt-12 flex items-center gap-6">
                <div className="flex -space-x-2">
                  {[1, 2].map((i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-full bg-gradient-to-br from-[#464B8B] to-[#00C8EB] border-2 border-white dark:border-[#0A0A10] flex items-center justify-center text-white text-xs font-bold"
                    >
                      {i}
                    </div>
                  ))}
                </div>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  <span className="font-semibold text-[#00C8EB]">2+</span> enterprises trust ATLAS
                </p>
              </div>
            </motion.div>

            {/* Right visual - Image Carousel */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-[#464B8B]/20 to-[#00C8EB]/20 rounded-3xl blur-3xl" />
                <div className="relative rounded-3xl shadow-2xl border border-white/10 overflow-hidden aspect-square">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={currentImageIndex}
                      src={heroImages[currentImageIndex]}
                      alt="ATLAS SOFTECH"
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.7 }}
                      className="w-full h-full object-cover"
                    />
                  </AnimatePresence>
                </div>
                {/* Image indicators */}
                <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-[#00C8EB] w-6' 
                          : theme === 'dark' ? 'bg-white/30' : 'bg-gray-300'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <StatsSection />

      {/* Services Overview */}
      <section 
        data-testid="services-section"
        className={`py-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#00C8EB] font-medium uppercase tracking-wider text-sm">
              Our Services
            </span>
            <h2 className={`text-3xl md:text-4xl font-bold mt-3 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Intelligent Solutions at Scale
            </h2>
            <p className={`mt-4 max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              High Secure technology designed to propel your business or institution
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
                className={`service-card group p-6 rounded-2xl border transition-all duration-300 ${
                  theme === 'dark'
                    ? 'bg-[#12131F] border-[#464B8B]/20 hover:border-[#00C8EB]/50 hover:shadow-[0_0_30px_rgba(0,200,235,0.1)]'
                    : 'bg-white border-gray-200 hover:border-[#464B8B]/50 hover:shadow-xl'
                }`}
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: `${service.color}20` }}
                >
                  <service.icon size={28} weight="duotone" style={{ color: service.color }} />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${
                  theme === 'dark' ? 'text-white' : 'text-gray-900'
                }`}>
                  {service.title}
                </h3>
                <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                  {service.description}
                </p>
                <Link 
                  to="/solutions" 
                  className="inline-flex items-center gap-2 mt-4 text-[#00C8EB] font-medium group-hover:gap-3 transition-all"
                >
                  Learn more <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/solutions">
              <Button
                data-testid="view-all-solutions"
                variant="outline"
                size="lg"
                className={`font-semibold ${
                  theme === 'dark'
                    ? 'border-[#464B8B] text-white hover:bg-[#464B8B]/20'
                    : 'border-[#464B8B] text-[#464B8B] hover:bg-[#464B8B]/10'
                }`}
              >
                View All Solutions
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Projects Section */}
      <section 
        data-testid="projects-section"
        className={`py-24 ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 rounded-full bg-[#00C8EB]/10 text-[#00C8EB] text-sm font-medium mb-4">
              Portfolio
            </span>
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>
              Our <span className="text-gradient">Projects</span>
            </h2>
            <p className={`text-lg max-w-2xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              Discover the innovative solutions we've built for our clients
            </p>
          </motion.div>

          {/* Sokoni POS Project */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`rounded-3xl overflow-hidden ${
              theme === 'dark' ? 'bg-[#12131F] border border-[#464B8B]/20' : 'bg-gray-50 border border-gray-200'
            }`}
          >
            <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
              {/* Project Info */}
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <img 
                    src="https://sokonipos.store/wp-content/uploads/2025/07/favicon-1.png" 
                    alt="Sokoni POS Logo" 
                    className="w-12 h-12 rounded-xl"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div>
                    <h3 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      Sokoni POS
                    </h3>
                    <span className="text-[#00C8EB] text-sm font-medium">Point of Sale System</span>
                  </div>
                </div>
                
                <p className={`text-lg mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                  AI powered streamlined point-of-sale, inventory tracking, sales, revenue and expenses management system designed for small, medium and large businesses to track stock, process sales, and visualize profitability through real-time dashboards. Sokoni POS operates ONLINE and OFFLINE, giving the business owner full access to the system at all times.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Features</p>
                    <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>AI Analytics</p>
                  </div>
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Platform</p>
                    <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Mobile App</p>
                  </div>
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Mode</p>
                    <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Online & Offline</p>
                  </div>
                  <div className={`p-4 rounded-xl ${theme === 'dark' ? 'bg-[#0A0A10]' : 'bg-white'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Payments</p>
                    <p className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>M-Pesa & Cash</p>
                  </div>
                </div>

                <a 
                  href="https://sokonipos.store/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex"
                >
                  <Button
                    data-testid="view-sokoni-pos"
                    className="bg-gradient-to-r from-[#464B8B] to-[#00C8EB] text-white font-semibold px-8"
                  >
                    View Project
                    <ArrowRight size={20} className="ml-2" />
                  </Button>
                </a>
              </div>

              {/* Project Images Carousel */}
              <div className="relative">
                <div className="grid grid-cols-1 gap-4">
                  <img 
                    src="https://customer-assets-eiarnc6j.emergentagent.net/job_atlas-enterprise-hub/artifacts/0ej1ahx8_sokoni-pos-play-feature-01-all-in-one-1024x500.png"
                    alt="Sokoni POS - All in One"
                    className="w-full rounded-xl shadow-2xl"
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <img 
                      src="https://customer-assets-eiarnc6j.emergentagent.net/job_atlas-enterprise-hub/artifacts/xe3slksu_sokoni-pos-play-feature-02-ai-analytics-1024x500.png"
                      alt="Sokoni POS - AI Analytics"
                      className="w-full rounded-xl shadow-lg"
                    />
                    <img 
                      src="https://customer-assets-eiarnc6j.emergentagent.net/job_atlas-enterprise-hub/artifacts/j81p5zc5_sokoni-pos-play-feature-04-quick-sale-1024x500.png"
                      alt="Sokoni POS - Quick Sale"
                      className="w-full rounded-xl shadow-lg"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        data-testid="cta-section"
        className="py-24 bg-gradient-to-r from-[#464B8B] to-[#00C8EB] relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Infrastructure?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss how ATLAS SOFTECH can help you achieve your technology goals. 
              Our experts are ready to design solutions tailored to your needs.
            </p>
            <Link to="/contact">
              <Button
                data-testid="cta-contact-btn"
                size="lg"
                className="bg-white text-[#464B8B] hover:bg-white/90 font-semibold px-8"
              >
                Schedule a Consultation
                <ArrowRight size={20} className="ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
